import { useEffect } from "react";

/**
 * Global Escape-key handler — mounted once at the app root.
 *
 * On Escape, locates the topmost full-viewport overlay (`div.fixed.inset-0`)
 * and dispatches a click on its close affordance. Recognised affordances
 * (in priority order):
 *
 *   1. button[data-escape-close="true"]       — explicit opt-in
 *   2. button[aria-label="Close"]              — accessible default
 *   3. button[aria-label*="close" i]           — any “close” aria label
 *   4. button[data-testid$="-close"]           — testid convention
 *   5. the topmost overlay's first `div[onclick]`-style backdrop
 *
 * Any modal in the codebase that already exposes one of these gets Escape-to-close
 * for free, without needing to thread state through its parent component. Shadcn
 * Radix dialogs handle their own Escape — this handler skips them by looking only
 * for `.fixed.inset-0` (custom overlays).
 */
export default function GlobalEscapeHandler() {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      // Topmost overlay = last-mounted .fixed.inset-0
      const overlays = document.querySelectorAll("div.fixed.inset-0");
      if (overlays.length === 0) return;
      const top = overlays[overlays.length - 1];

      // 1) explicit opt-in
      let target = top.querySelector('button[data-escape-close="true"]');
      // 2-4) accessible / testid close buttons
      if (!target) target = top.querySelector('button[aria-label="Close"]');
      if (!target) target = top.querySelector('button[aria-label*="close" i]');
      if (!target) target = top.querySelector('button[data-testid$="-close"]');

      if (target) {
        e.preventDefault();
        e.stopPropagation();
        target.click();
      }
    };
    // capture-phase so we win over input/textarea handlers that swallow Esc
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return null;
}
